import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { fetchOverview } from '@/lib/admin/overview';
import { fetchAlerts } from '@/lib/admin/alerts';
import type { AlertSeverity } from '@/types/admin';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import {
  UsersIcon,
  ArrowTrendingUpIcon,
  CurrencyEuroIcon,
  TicketIcon,
  ExclamationTriangleIcon,
  FireIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import PageSkeleton from '@/components/PageSkeleton';

const COLORS = ['#6366f1', '#22c55e', '#f97316', '#0ea5e9', '#a855f7', '#ef4444', '#eab308'];

function Kpi({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string | number;
  icon: typeof UsersIcon;
  accent: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</span>
        <div className={`rounded-lg p-2 ${accent}`}>
          <Icon className="h-4 w-4 text-white" />
        </div>
      </div>
      <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

export default function OverviewPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: fetchOverview,
  });

  const { data: recentAlerts } = useQuery({
    queryKey: ['admin', 'overview', 'recent-alerts'],
    queryFn: () => fetchAlerts({ perPage: 5 }),
  });

  if (isLoading) return <PageSkeleton variant="cards" />;
  if (isError || !data) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        {t('common.error')}
      </div>
    );
  }

  const subs = data.subscriptions ?? { countByPlan: {}, countByStatus: {}, mrr: 0, trialEndingThisWeek: [], trialCount: 0 };
  const totals = data.totals ?? { users: 0, organizations: 0, activeUsers7d: 0, newSignups7d: 0, newSignups30d: 0 };
  const signups = data.signups ?? { last7d: 0, last30d: 0 };
  const topErrors = data.topErrors ?? [];
  const trialEnding = subs.trialEndingThisWeek ?? [];

  const planData = Object.entries(subs.countByPlan ?? {}).map(([name, value]) => ({
    name,
    value,
  }));
  const statusData = Object.entries(subs.countByStatus ?? {}).map(([name, value]) => ({
    name,
    value,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.nav.overview')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('admin.overview.subtitle')}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Kpi
          label={t('admin.kpis.users')}
          value={totals.users ?? 0}
          icon={UsersIcon}
          accent="bg-indigo-500"
        />
        <Kpi
          label={t('admin.kpis.active7d')}
          value={totals.activeUsers7d ?? 0}
          icon={ChartBarIcon}
          accent="bg-emerald-500"
        />
        <Kpi
          label={t('admin.kpis.signups7d')}
          value={signups.last7d ?? 0}
          icon={ArrowTrendingUpIcon}
          accent="bg-sky-500"
        />
        <Kpi
          label={t('admin.kpis.mrr')}
          value={`€${Number(subs.mrr || 0).toFixed(0)}`}
          icon={CurrencyEuroIcon}
          accent="bg-amber-500"
        />
        <Kpi
          label={t('admin.kpis.openTickets')}
          value={data.openTickets ?? 0}
          icon={TicketIcon}
          accent="bg-violet-500"
        />
        <Kpi
          label={t('admin.kpis.errors24h')}
          value={data.errorsLast24h ?? 0}
          icon={ExclamationTriangleIcon}
          accent="bg-rose-500"
        />
        <Kpi
          label={t('admin.kpis.hotLeads')}
          value={data.hotLeadsCount ?? 0}
          icon={FireIcon}
          accent="bg-orange-500"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">{t('admin.overview.planDistribution')}</h3>
          {planData.length ? (
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={planData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90}>
                    {planData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t('common.noResults')}</p>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">{t('admin.overview.statusDistribution')}</h3>
          {statusData.length ? (
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={statusData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#6366f1" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t('common.noResults')}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">{t('admin.overview.topErrors')}</h3>
          {topErrors.length === 0 ? (
            <p className="text-sm text-gray-500">{t('common.noResults')}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {topErrors.map((e) => (
                <li key={e.signature} className="py-2">
                  <div className="flex items-center justify-between">
                    <span className="truncate text-sm font-medium text-gray-900">
                      {e.sampleMessage || e.signature}
                    </span>
                    <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
                      {e.count}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">{e.signature}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-amber-900">
            {t('admin.overview.trialsEndingThisWeek')}{' '}
            <span className="ml-1 rounded-full bg-amber-200 px-2 py-0.5 text-xs">
              {trialEnding.length}
            </span>
          </h3>
          {trialEnding.length === 0 ? (
            <p className="text-sm text-amber-800/70">{t('admin.overview.noTrialsEnding')}</p>
          ) : (
            <ul className="divide-y divide-amber-200/60">
              {trialEnding.map((u) => (
                <li key={u.userId} className="flex items-center justify-between py-2 text-sm">
                  <a
                    className="font-medium text-amber-900 hover:underline"
                    href={`/admin/users/${u.userId}`}
                  >
                    {u.email}
                  </a>
                  <span className="text-xs text-amber-800">
                    {new Date(u.trialEndsAt).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">{t('admin.overview.recentMovements')}</h3>
          <Link to="/admin/alerts" className="text-xs font-medium text-indigo-600 hover:underline">
            {t('admin.alerts.viewAll')}
          </Link>
        </div>
        {(recentAlerts?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-gray-500">{t('admin.alerts.noAlerts')}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {(recentAlerts?.items ?? []).map((a) => {
              const dot: Record<AlertSeverity, string> = {
                INFO: 'bg-sky-500',
                WARNING: 'bg-amber-500',
                CRITICAL: 'bg-rose-500',
              };
              return (
                <li key={a.id} className="flex items-start gap-3 py-2 text-sm">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dot[a.severity]}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                        {t(`admin.alerts.type.${a.type}`)}
                      </span>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs text-gray-500">{a.organization?.name ?? '—'}</span>
                    </div>
                    <p className="truncate text-sm text-gray-800">{a.message}</p>
                  </div>
                  <span className="shrink-0 text-xs text-gray-500">
                    {formatDistanceToNow(new Date(a.createdAt), { addSuffix: true })}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
